import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  ChevronLeft, 
  ChevronDown, 
  ChevronUp,
  FlaskConical,
  AlertTriangle,
  Activity,
  Bug,
  Shield,
  Heart,
  Microscope,
  Dna,
  Apple,
  TestTube,
  Beaker,
  Info
} from 'lucide-react';

const LabEducation = () => {
  const navigate = useNavigate();
  const [expandedCategories, setExpandedCategories] = useState({});

  const toggleCategory = (categoryKey) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryKey]: !prev[categoryKey]
    }));
  };

  const labCategories = {
    basic_panels: {
      name: 'Basic Panels',
      icon: <Activity className="w-5 h-5" />,
      color: 'bg-blue-500',
      lightColor: 'bg-blue-50',
      description: 'Foundational tests that provide a baseline picture of overall health and organ function.',
      tests: [
        {
          name: 'CBC with diff/plt',
          fullName: 'Complete Blood Count with Differential and Platelets',
          measures: 'Red blood cells, white blood cells (and types), hemoglobin, hematocrit, and platelets',
          relevance: 'Can reveal infections, anemia, immune system status, and inflammation. White cell differential helps identify bacterial vs viral infections.',
          frequency: 'At baseline and during flares; every 3-6 months when stable'
        },
        {
          name: 'Comprehensive Metabolic Panel (CMP)',
          fullName: 'Comprehensive Metabolic Panel',
          measures: 'Glucose, electrolytes, kidney function (BUN, creatinine), liver enzymes (AST, ALT), and proteins',
          relevance: 'Monitors overall metabolic health, organ function, and can detect issues that may worsen PANS symptoms.',
          frequency: 'At baseline; every 6-12 months or as needed'
        },
        {
          name: 'Iron/TIBC',
          fullName: 'Iron and Total Iron Binding Capacity',
          measures: 'Serum iron levels and the blood\'s capacity to bind iron',
          relevance: 'Iron deficiency can worsen neurological symptoms, fatigue, and cognitive function. Important for brain health.',
          frequency: 'At baseline; repeat if symptoms suggest deficiency'
        },
        {
          name: 'Ferritin',
          fullName: 'Serum Ferritin',
          measures: 'Iron storage protein; reflects body\'s iron reserves',
          relevance: 'Low ferritin can cause fatigue, cognitive issues, and restless legs. High ferritin can indicate inflammation.',
          frequency: 'At baseline; every 6 months if supplementing iron'
        },
        {
          name: 'TSH',
          fullName: 'Thyroid Stimulating Hormone',
          measures: 'Pituitary hormone that regulates thyroid function',
          relevance: 'Thyroid dysfunction can mimic or worsen PANS symptoms (anxiety, mood changes, fatigue, cognitive issues).',
          frequency: 'At baseline; annually or if symptoms suggest thyroid issues'
        },
        {
          name: 'Free T4',
          fullName: 'Free Thyroxine',
          measures: 'Active form of thyroid hormone T4',
          relevance: 'Provides a more complete picture of thyroid function than TSH alone.',
          frequency: 'With TSH at baseline; as needed'
        },
        {
          name: 'Free T3',
          fullName: 'Free Triiodothyronine',
          measures: 'Active form of thyroid hormone T3',
          relevance: 'T3 is the most active thyroid hormone. Some patients have poor T4 to T3 conversion.',
          frequency: 'At baseline; as needed based on symptoms'
        },
        {
          name: 'Reverse T3',
          fullName: 'Reverse Triiodothyronine',
          measures: 'Inactive form of T3',
          relevance: 'Elevated in chronic illness and stress. Can indicate thyroid conversion issues.',
          frequency: 'If thyroid symptoms persist despite normal other labs'
        },
        {
          name: 'Ceruloplasmin',
          fullName: 'Serum Ceruloplasmin',
          measures: 'Copper-carrying protein',
          relevance: 'Helps assess copper status. Copper imbalance can affect neurological function and mood.',
          frequency: 'At baseline; as needed'
        },
        {
          name: 'Lipid Panel',
          fullName: 'Lipid Panel',
          measures: 'Total cholesterol, LDL, HDL, and triglycerides',
          relevance: 'Cholesterol is essential for brain function and hormone production. Very low levels can be problematic.',
          frequency: 'At baseline; annually'
        }
      ]
    },
    inflammation: {
      name: 'General Inflammation',
      icon: <Activity className="w-5 h-5" />,
      color: 'bg-orange-500',
      lightColor: 'bg-orange-50',
      description: 'Markers that detect inflammation in the body, which is central to PANDAS/PANS.',
      tests: [
        {
          name: 'Hs-CRP',
          fullName: 'High-Sensitivity C-Reactive Protein',
          measures: 'A protein produced by the liver in response to inflammation',
          relevance: 'Elevated in active inflammation. Can help track flares and response to treatment. More sensitive than standard CRP.',
          frequency: 'At baseline, during flares, and to monitor treatment response'
        },
        {
          name: 'D-Dimer',
          fullName: 'D-Dimer',
          measures: 'Fibrin degradation product; indicates clot breakdown',
          relevance: 'Can be elevated in inflammation and certain autoimmune conditions. Sometimes elevated in PANS flares.',
          frequency: 'At baseline; during significant flares'
        },
        {
          name: 'Prostaglandin D2',
          fullName: 'Prostaglandin D2',
          measures: 'Inflammatory mediator released by mast cells',
          relevance: 'Elevated in mast cell activation. Helps identify mast cell involvement in symptoms.',
          frequency: 'If mast cell activation suspected; during reactions'
        }
      ]
    },
    infection: {
      name: 'Infection Markers',
      icon: <Bug className="w-5 h-5" />,
      color: 'bg-red-500',
      lightColor: 'bg-red-50',
      description: 'Tests to identify current or past infections that may trigger or perpetuate PANDAS/PANS.',
      tests: [
        {
          name: 'Rapid Strep Antigen Test',
          fullName: 'Rapid Strep Antigen Test',
          measures: 'Detects Group A Streptococcus antigens from throat swab',
          relevance: 'Quick screening for active strep. Negative result doesn\'t rule out strep (30% false negative rate).',
          frequency: 'During acute illness; when strep exposure suspected'
        },
        {
          name: 'Strep Culture (throat)',
          fullName: 'Throat Culture for Group A Streptococcus',
          measures: 'Gold standard for detecting strep bacteria',
          relevance: 'More accurate than rapid test but takes 24-48 hours. Should be done if rapid test negative but strep suspected.',
          frequency: 'During acute illness; follow rapid test if negative'
        },
        {
          name: 'Anti-DNase B Strep ab',
          fullName: 'Anti-Deoxyribonuclease B Antibody',
          measures: 'Antibodies against strep enzyme; indicates past infection',
          relevance: 'Rises 6-8 weeks after strep infection. Better indicator of strep-triggered PANDAS than ASO.',
          frequency: 'At baseline; during suspected flares; every 3-6 months'
        },
        {
          name: 'ASO Strep ab',
          fullName: 'Anti-Streptolysin O',
          measures: 'Antibodies against strep toxin',
          relevance: 'Rises 1-3 weeks after strep infection. May miss some strep infections. Use with Anti-DNase B.',
          frequency: 'At baseline; during suspected flares; every 3-6 months'
        },
        {
          name: 'Mycoplasma pneumoniae IgG/IgM',
          fullName: 'Mycoplasma pneumoniae Antibodies',
          measures: 'IgM indicates recent infection; IgG indicates past infection or immunity',
          relevance: 'Mycoplasma is a major PANS trigger. Can cause persistent infection. Check both acute and convalescent titers.',
          frequency: 'At baseline; during respiratory illness; every 6 months if history'
        },
        {
          name: 'Chlamydia pneumonia IgG/IgM/IgA',
          fullName: 'Chlamydia pneumoniae Antibodies',
          measures: 'Antibodies to Chlamydia pneumoniae (not STI chlamydia)',
          relevance: 'Another bacterial trigger for PANS. Can cause chronic infection. IgA suggests active/recent infection.',
          frequency: 'At baseline; as clinically indicated'
        },
        {
          name: 'Toxoplasma gondii IgG and IgM',
          fullName: 'Toxoplasma Antibodies',
          measures: 'Antibodies to Toxoplasma parasite',
          relevance: 'Can cause neuropsychiatric symptoms. Consider if cat exposure or undercooked meat consumption.',
          frequency: 'At baseline; as clinically indicated'
        },
        {
          name: 'EBV Antibody Profile',
          fullName: 'Epstein-Barr Virus Antibody Panel',
          measures: 'VCA IgM, VCA IgG, EBNA antibodies',
          relevance: 'Helps determine if EBV infection is acute, recent, or reactivated. EBV can trigger PANS.',
          frequency: 'At baseline; if fatigue or mononucleosis symptoms'
        },
        {
          name: 'EBV Early Antigen',
          fullName: 'EBV Early Antigen Antibody',
          measures: 'Antibodies to early EBV proteins',
          relevance: 'Suggests active or reactivated EBV infection. Often elevated in chronic active EBV.',
          frequency: 'If EBV reactivation suspected'
        },
        {
          name: 'Pneumococcal Ab Titers',
          fullName: 'Pneumococcal Antibody Titers',
          measures: 'Antibodies to pneumococcal serotypes',
          relevance: 'Assesses vaccine response and immune function. Low titers may indicate immunodeficiency.',
          frequency: 'At baseline to assess immune function'
        },
        {
          name: 'Tetanus/Diph Ab Titers',
          fullName: 'Tetanus and Diphtheria Antibody Titers',
          measures: 'Antibodies to tetanus and diphtheria toxins',
          relevance: 'Assesses vaccine response. Poor response may indicate immune dysfunction.',
          frequency: 'At baseline to assess immune function'
        },
        {
          name: 'DNA PCR Molds',
          fullName: 'Mold DNA PCR Testing',
          measures: 'Detects mold DNA in urine or nasal samples',
          relevance: 'Mold exposure can trigger mast cell activation and worsen PANS. Important if home water damage suspected.',
          frequency: 'If mold exposure suspected; after remediation'
        }
      ]
    },
    autoimmunity: {
      name: 'Autoimmunity',
      icon: <Shield className="w-5 h-5" />,
      color: 'bg-purple-500',
      lightColor: 'bg-purple-50',
      description: 'Tests that detect autoimmune activity which may be driving PANDAS/PANS symptoms.',
      tests: [
        {
          name: 'Celiac Antibody Panel',
          fullName: 'Celiac Disease Antibody Panel',
          measures: 'tTG-IgA, EMA, DGP antibodies',
          relevance: 'Celiac disease is an autoimmune condition with neuropsychiatric manifestations. More common in autoimmune-prone individuals.',
          frequency: 'At baseline; consider if GI symptoms or family history'
        },
        {
          name: 'Celiac Disease HLA (DQ2/DQ8)',
          fullName: 'HLA-DQ2 and HLA-DQ8 Genetic Testing',
          measures: 'Genetic markers associated with celiac disease',
          relevance: 'Negative result essentially rules out celiac disease. Positive doesn\'t confirm celiac but indicates risk.',
          frequency: 'Once (genetic test)'
        },
        {
          name: 'ANA with Reflex Panel',
          fullName: 'Antinuclear Antibody with Reflex to Specific Antibodies',
          measures: 'Antibodies against cell nuclei; reflex tests identify specific types',
          relevance: 'Screens for systemic autoimmune diseases (lupus, Sjögren\'s, etc.) that can cause neuropsychiatric symptoms.',
          frequency: 'At baseline; repeat if clinical picture changes'
        },
        {
          name: 'Anti-Thyroglobulin AB',
          fullName: 'Anti-Thyroglobulin Antibodies',
          measures: 'Antibodies against thyroid protein thyroglobulin',
          relevance: 'Indicates autoimmune thyroid disease. Hashimoto\'s encephalopathy can mimic PANS.',
          frequency: 'At baseline; with thyroid panel'
        },
        {
          name: 'TPO Antibodies',
          fullName: 'Thyroid Peroxidase Antibodies',
          measures: 'Antibodies against thyroid enzyme',
          relevance: 'Most common marker for Hashimoto\'s thyroiditis. Autoimmune thyroid disease is common in PANS families.',
          frequency: 'At baseline; with thyroid panel'
        },
        {
          name: 'RF',
          fullName: 'Rheumatoid Factor',
          measures: 'Autoantibody associated with rheumatoid arthritis and other conditions',
          relevance: 'Can be elevated in various autoimmune conditions. Sometimes elevated in post-strep conditions.',
          frequency: 'At baseline; as clinically indicated'
        },
        {
          name: 'Anti-CCP ab (IgA/IgG)',
          fullName: 'Anti-Cyclic Citrullinated Peptide Antibodies',
          measures: 'Specific marker for rheumatoid arthritis',
          relevance: 'More specific than RF for rheumatoid arthritis. Consider if joint symptoms present.',
          frequency: 'If joint symptoms present'
        },
        {
          name: 'Complement C4a',
          fullName: 'Complement C4a',
          measures: 'Split product of complement activation',
          relevance: 'Elevated in mold illness (CIRS) and inflammatory conditions. Can help identify biotoxin illness.',
          frequency: 'If mold illness suspected'
        },
        {
          name: 'Human TGFb-1',
          fullName: 'Transforming Growth Factor Beta-1',
          measures: 'Cytokine involved in immune regulation and fibrosis',
          relevance: 'Often elevated in mold illness. Can indicate chronic inflammatory response.',
          frequency: 'If mold illness suspected'
        },
        {
          name: 'Autoimmune Encephalitis Antibodies',
          fullName: 'Autoimmune Encephalitis Panel',
          measures: 'Antibodies against neuronal proteins (NMDA receptor, VGKC, GAD65, etc.)',
          relevance: 'Detects antibody-mediated encephalitis which can present similarly to PANS. Important differential diagnosis.',
          frequency: 'At baseline or if presentation severe/atypical'
        }
      ]
    },
    immune_allergic: {
      name: 'Immune and Allergic',
      icon: <Heart className="w-5 h-5" />,
      color: 'bg-pink-500',
      lightColor: 'bg-pink-50',
      description: 'Tests to evaluate immune system function and allergic/mast cell disorders.',
      tests: [
        {
          name: 'Environmental Allergies IgE',
          fullName: 'Environmental Allergen Panel IgE',
          measures: 'IgE antibodies to common environmental allergens',
          relevance: 'Identifies allergic triggers that may activate mast cells and worsen symptoms.',
          frequency: 'At baseline; if allergic symptoms present'
        },
        {
          name: 'Mold Allergies IgE',
          fullName: 'Mold Allergen Panel IgE',
          measures: 'IgE antibodies to common mold species',
          relevance: 'Identifies mold allergy (different from mold toxicity/CIRS). Can worsen MCAS.',
          frequency: 'If mold exposure or allergy suspected'
        },
        {
          name: 'IgA, IgG, IgM Quant',
          fullName: 'Quantitative Immunoglobulins',
          measures: 'Levels of main antibody classes',
          relevance: 'Identifies immunodeficiencies (low IgA common) that may predispose to infections and PANS.',
          frequency: 'At baseline to assess immune function'
        },
        {
          name: 'IgG Subclasses 1-4',
          fullName: 'IgG Subclass Panel',
          measures: 'Levels of IgG subclasses 1, 2, 3, and 4',
          relevance: 'IgG subclass deficiency can cause recurrent infections even with normal total IgG. Common in PANS.',
          frequency: 'At baseline; if recurrent infections'
        },
        {
          name: 'IgA Subclasses',
          fullName: 'IgA Subclass Panel',
          measures: 'IgA1 and IgA2 levels',
          relevance: 'IgA deficiency is most common immunodeficiency. Affects mucosal immunity.',
          frequency: 'If total IgA low or borderline'
        },
        {
          name: 'Tryptase',
          fullName: 'Serum Tryptase',
          measures: 'Enzyme released by mast cells',
          relevance: 'Elevated in mast cell activation. Baseline level helps diagnose MCAS. Check during reactions.',
          frequency: 'Baseline (when well) and during reactions (within 4 hours)'
        }
      ]
    },
    tick_bourne: {
      name: 'Specialty Tick-Borne Testing',
      icon: <Microscope className="w-5 h-5" />,
      color: 'bg-green-600',
      lightColor: 'bg-green-50',
      description: 'Comprehensive testing for tick-borne infections that can cause or mimic PANS symptoms.',
      tests: [
        {
          name: 'Babesia Testing',
          fullName: 'Babesia Species Panel',
          measures: 'Immunoblot, IFA, FISH, and PCR for Babesia species',
          relevance: 'Babesia is a tick-borne parasite that causes malaria-like illness. Can trigger PANS and cause severe neuropsychiatric symptoms.',
          frequency: 'If tick exposure or compatible symptoms'
        },
        {
          name: 'Borrelia (Lyme) Testing',
          fullName: 'Borrelia burgdorferi Comprehensive Panel',
          measures: 'Western blot, ELISA, PCR, and specialty tests (IgXSpot)',
          relevance: 'Lyme disease causes neurological and psychiatric symptoms. Standard testing has high false-negative rate; specialty labs may be more sensitive.',
          frequency: 'At baseline if tick exposure possible; comprehensive testing if suspected'
        },
        {
          name: 'Bartonella Testing',
          fullName: 'Bartonella Species Panel',
          measures: 'Immunoblot, IFA, PCR, FISH, IgXSpot',
          relevance: 'Bartonella can cause significant neuropsychiatric symptoms including anxiety, rage, cognitive issues. Often co-occurs with Lyme.',
          frequency: 'If tick/flea/cat exposure or compatible symptoms'
        },
        {
          name: 'Other Tick-Borne',
          fullName: 'Additional Tick-Borne Infections',
          measures: 'Anaplasma, Ehrlichia, Rickettsia testing',
          relevance: 'These infections can cause acute illness and may contribute to immune dysfunction.',
          frequency: 'As clinically indicated'
        },
        {
          name: 'Specialty Markers',
          fullName: 'Anti-Neuronal Antibodies (Tick-Related)',
          measures: 'Anti-lysoganglioside GM-1, Anti-tubulin, CaMKII',
          relevance: 'These antibodies can indicate autoimmune response to neuronal tissues, sometimes triggered by tick-borne infections.',
          frequency: 'If neurological symptoms with tick-borne history'
        }
      ]
    },
    stool_testing: {
      name: 'Stool Testing',
      icon: <TestTube className="w-5 h-5" />,
      color: 'bg-amber-600',
      lightColor: 'bg-amber-50',
      description: 'Tests to evaluate gut health, which is closely connected to immune function and brain health.',
      tests: [
        {
          name: 'Stool Culture',
          fullName: 'Stool Culture for Pathogens',
          measures: 'Bacterial pathogens in stool',
          relevance: 'Identifies bacterial infections that may be triggering or perpetuating PANS.',
          frequency: 'If GI symptoms present; at baseline'
        },
        {
          name: 'O&P (Ova and Parasites)',
          fullName: 'Stool Ova and Parasite Examination',
          measures: 'Parasites and their eggs in stool',
          relevance: 'Parasitic infections can cause immune dysfunction and neurological symptoms.',
          frequency: 'At baseline; if GI symptoms or travel history'
        },
        {
          name: 'Yeast',
          fullName: 'Stool Yeast/Fungal Culture',
          measures: 'Candida and other fungal overgrowth',
          relevance: 'Yeast overgrowth can worsen PANS symptoms and indicate gut dysbiosis.',
          frequency: 'At baseline; if antibiotic history or yeast symptoms'
        },
        {
          name: 'Calprotectin',
          fullName: 'Fecal Calprotectin',
          measures: 'Marker of intestinal inflammation',
          relevance: 'Elevated indicates gut inflammation. Helps differentiate inflammatory bowel disease from IBS.',
          frequency: 'If GI symptoms present'
        },
        {
          name: 'Stool Blood',
          fullName: 'Fecal Occult Blood',
          measures: 'Hidden blood in stool',
          relevance: 'Indicates GI bleeding which can occur with inflammation or infection.',
          frequency: 'If GI symptoms present'
        },
        {
          name: 'Fatty Acid Analysis',
          fullName: 'Short-Chain Fatty Acids and Fat Stain',
          measures: 'Fatty acid levels and fat malabsorption',
          relevance: 'Indicates gut microbiome health and digestive function. SCFAs important for gut-brain connection.',
          frequency: 'Part of comprehensive stool analysis'
        }
      ]
    },
    nasal_testing: {
      name: 'Nasal Testing',
      icon: <Beaker className="w-5 h-5" />,
      color: 'bg-teal-500',
      lightColor: 'bg-teal-50',
      description: 'Tests to evaluate nasal colonization and chronic sinus infections.',
      tests: [
        {
          name: 'Nasal Swab',
          fullName: 'Nasal Swab Culture',
          measures: 'Bacteria present in nasal passages',
          relevance: 'Identifies Staph or Strep colonization that may serve as reservoir for reinfection.',
          frequency: 'If recurrent infections; at baseline'
        },
        {
          name: 'Nasal Bacterial Culture',
          fullName: 'Deep Nasal/Sinus Bacterial Culture',
          measures: 'Bacteria in deeper nasal/sinus tissues',
          relevance: 'Chronic sinus infections can be hidden source of ongoing infection and inflammation.',
          frequency: 'If chronic sinusitis suspected'
        },
        {
          name: 'Nasal Fungal Culture',
          fullName: 'Nasal Fungal Culture',
          measures: 'Fungal organisms in nasal passages',
          relevance: 'Fungal sinusitis can occur especially with mold exposure. Can perpetuate chronic illness.',
          frequency: 'If mold exposure or chronic sinusitis'
        },
        {
          name: 'Biofilm Analysis',
          fullName: 'Nasal Biofilm Testing',
          measures: 'Biofilm-forming organisms',
          relevance: 'Biofilms protect bacteria from antibiotics and immune system. May explain treatment resistance.',
          frequency: 'If chronic infections not responding to treatment'
        }
      ]
    },
    cunningham_panel: {
      name: 'Cunningham Panel',
      icon: <FlaskConical className="w-5 h-5" />,
      color: 'bg-indigo-500',
      lightColor: 'bg-indigo-50',
      description: 'Specialized panel designed specifically for PANDAS/PANS that measures autoantibodies against brain targets.',
      tests: [
        {
          name: 'Anti-Dopamine D1 Receptor',
          fullName: 'Anti-Dopamine D1 Receptor Antibodies',
          measures: 'Antibodies targeting dopamine D1 receptors in the brain',
          relevance: 'Elevated antibodies may interfere with dopamine signaling, contributing to movement disorders and behavioral symptoms.',
          frequency: 'At diagnosis; to monitor treatment response'
        },
        {
          name: 'Anti-Dopamine D2L Receptor',
          fullName: 'Anti-Dopamine D2L Receptor Antibodies',
          measures: 'Antibodies targeting dopamine D2 long receptors',
          relevance: 'D2 receptors are involved in movement, motivation, and reward. Antibodies may cause tics and movement issues.',
          frequency: 'At diagnosis; to monitor treatment response'
        },
        {
          name: 'Anti-Lysoganglioside',
          fullName: 'Anti-Lysoganglioside GM1 Antibodies',
          measures: 'Antibodies against neuronal membrane components',
          relevance: 'Can indicate autoimmune attack on neurons. Associated with neurological dysfunction.',
          frequency: 'At diagnosis; to monitor treatment response'
        },
        {
          name: 'Anti-Tubulin',
          fullName: 'Anti-Tubulin Antibodies',
          measures: 'Antibodies against tubulin (structural protein in neurons)',
          relevance: 'Tubulin is essential for neuronal function. Antibodies may disrupt neural signaling.',
          frequency: 'At diagnosis; to monitor treatment response'
        },
        {
          name: 'CaMKII Activity',
          fullName: 'Ca/Calmodulin Protein Kinase II Stimulation Assay',
          measures: 'Activity level of CaMKII enzyme when exposed to patient serum',
          relevance: 'CaMKII is involved in neuronal signaling. Elevated activity suggests autoantibodies are causing neuronal hyperactivation.',
          frequency: 'At diagnosis; to monitor treatment response'
        }
      ]
    },
    micronutrients: {
      name: 'Micronutrients & Vitamins',
      icon: <Apple className="w-5 h-5" />,
      color: 'bg-lime-500',
      lightColor: 'bg-lime-50',
      description: 'Tests to identify nutritional deficiencies that can affect immune function and neurological health.',
      tests: [
        {
          name: 'Copper',
          fullName: 'Serum Copper',
          measures: 'Copper levels in blood',
          relevance: 'Copper is essential for DAO enzyme (histamine breakdown) and neurological function. Must balance with zinc.',
          frequency: 'At baseline; if supplementing'
        },
        {
          name: 'Homocysteine',
          fullName: 'Plasma Homocysteine',
          measures: 'Amino acid that accumulates with poor methylation',
          relevance: 'Elevated levels indicate methylation problems (often MTHFR-related). Associated with inflammation and neurological issues.',
          frequency: 'At baseline; monitor if treating'
        },
        {
          name: 'Folate',
          fullName: 'Serum or RBC Folate',
          measures: 'Vitamin B9 levels',
          relevance: 'Essential for methylation and DNA synthesis. Deficiency common with MTHFR mutations.',
          frequency: 'At baseline; with methylation workup'
        },
        {
          name: 'Glutathione',
          fullName: 'Glutathione (reduced)',
          measures: 'Master antioxidant levels',
          relevance: 'Critical for detoxification and reducing oxidative stress. Often depleted in chronic illness.',
          frequency: 'At baseline; if oxidative stress suspected'
        },
        {
          name: 'Vitamin D, 25-OH',
          fullName: '25-Hydroxy Vitamin D',
          measures: 'Vitamin D storage form',
          relevance: 'Critical for immune function and mast cell stability. Deficiency very common in PANS. Aim for 50-80 ng/mL.',
          frequency: 'At baseline; every 3 months when supplementing; annually when stable'
        },
        {
          name: 'Methylmalonic Acid',
          fullName: 'Methylmalonic Acid (MMA)',
          measures: 'Marker of B12 deficiency at cellular level',
          relevance: 'More sensitive than serum B12. Elevated indicates functional B12 deficiency even if serum B12 normal.',
          frequency: 'If B12 deficiency suspected despite normal serum B12'
        },
        {
          name: 'RBC Magnesium',
          fullName: 'Red Blood Cell Magnesium',
          measures: 'Intracellular magnesium levels',
          relevance: 'More accurate than serum magnesium. Deficiency causes anxiety, sleep issues, muscle cramps.',
          frequency: 'At baseline; if supplementing'
        },
        {
          name: 'RBC Zinc',
          fullName: 'Red Blood Cell Zinc',
          measures: 'Intracellular zinc levels',
          relevance: 'Essential for immune function, DAO enzyme, and hundreds of enzymatic processes. Deficiency common.',
          frequency: 'At baseline; if supplementing'
        },
        {
          name: 'Vitamin B6',
          fullName: 'Pyridoxal-5-Phosphate (P5P)',
          measures: 'Active form of vitamin B6',
          relevance: 'Cofactor for DAO enzyme and neurotransmitter synthesis. Deficiency can worsen histamine issues.',
          frequency: 'At baseline; with DAO assessment'
        },
        {
          name: 'Vitamin B12',
          fullName: 'Serum B12',
          measures: 'Vitamin B12 levels',
          relevance: 'Essential for neurological function and methylation. Deficiency causes neuropsychiatric symptoms.',
          frequency: 'At baseline; annually'
        },
        {
          name: 'Serum Heavy Metals',
          fullName: 'Heavy Metal Panel',
          measures: 'Lead, mercury, arsenic, cadmium levels',
          relevance: 'Heavy metal toxicity can cause neurological and immune dysfunction. Consider if exposure history.',
          frequency: 'If toxicity suspected'
        },
        {
          name: 'Metabolomix',
          fullName: 'Comprehensive Metabolic/Nutritional Panel',
          measures: 'Organic acids, amino acids, fatty acids, oxidative stress markers',
          relevance: 'Provides comprehensive view of metabolic function and nutritional status.',
          frequency: 'At baseline for comprehensive assessment'
        }
      ]
    },
    genetics: {
      name: 'Genetics',
      icon: <Dna className="w-5 h-5" />,
      color: 'bg-cyan-500',
      lightColor: 'bg-cyan-50',
      description: 'Genetic tests that can identify predispositions affecting PANDAS/PANS and treatment response.',
      tests: [
        {
          name: 'MTHFR',
          fullName: 'MTHFR Gene Mutations (C677T, A1298C)',
          measures: 'Common genetic variants affecting methylation',
          relevance: 'Affects methylation, detoxification, and neurotransmitter production. Guides supplement choices (methylfolate vs folic acid).',
          frequency: 'Once (genetic test)'
        },
        {
          name: 'COMT',
          fullName: 'COMT Gene Variants',
          measures: 'Genetic variants affecting dopamine and estrogen metabolism',
          relevance: 'Affects how quickly neurotransmitters are broken down. Can influence anxiety, stress response, and medication metabolism.',
          frequency: 'Once (genetic test)'
        },
        {
          name: 'Whole Exome Sequencing',
          fullName: 'Whole Exome Sequencing (WES)',
          measures: 'Sequences all protein-coding genes',
          relevance: 'Can identify rare genetic variants that may contribute to immune dysfunction or neurological susceptibility.',
          frequency: 'If atypical presentation or family history suggests genetic component'
        }
      ]
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 to-orange-50 pb-20">
      {/* Header */}
      <div className="bg-white border-b border-stone-100 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="p-2"
              data-testid="back-button"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-nunito font-bold text-[#2D2D2D]">Lab Education</h1>
              <p className="text-sm text-[#57534E]">Understanding PANDAS/PANS Laboratory Testing</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Disclaimer */}
        <Card className="bg-amber-50 border-amber-200 rounded-xl p-5" data-testid="disclaimer-card">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h2 className="font-semibold text-amber-800 mb-2">Important Disclaimer</h2>
              <p className="text-sm text-amber-900 leading-relaxed">
                This information is for <strong>educational purposes only</strong> and is not intended to diagnose, treat, or replace professional medical advice. 
                Laboratory test interpretation requires clinical context and should always be done by a qualified healthcare provider familiar with PANDAS/PANS. 
                Reference ranges vary between laboratories. Always discuss your child's results with their healthcare team.
              </p>
            </div>
          </div>
        </Card>

        {/* Introduction */}
        <Card className="bg-white rounded-xl border border-stone-100 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center">
              <FlaskConical className="w-6 h-6 text-accent" />
            </div>
            <div>
              <h2 className="text-lg font-nunito font-bold text-[#2D2D2D]">Laboratory Testing in PANDAS/PANS</h2>
              <p className="text-sm text-[#57534E]">A comprehensive guide to recommended tests</p>
            </div>
          </div>
          <p className="text-[#57534E] leading-relaxed">
            Laboratory testing plays an important role in the evaluation and monitoring of children with PANDAS/PANS. 
            These tests help identify underlying infections, assess immune function, detect autoimmune markers, and monitor treatment response. 
            Not every child needs every test—your healthcare provider will determine which tests are appropriate based on your child's specific presentation.
          </p>
        </Card>

        {/* Quick Navigation */}
        <Card className="bg-white rounded-xl border border-stone-100 p-6">
          <h3 className="font-semibold text-[#2D2D2D] mb-4">Quick Navigation</h3>
          <div className="flex flex-wrap gap-2">
            {Object.entries(labCategories).map(([key, category]) => (
              <Button
                key={key}
                variant="outline"
                size="sm"
                className={`${category.lightColor} border-0 hover:opacity-80`}
                onClick={() => {
                  setExpandedCategories(prev => ({ ...prev, [key]: true }));
                  document.getElementById(key)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
              >
                {category.icon}
                <span className="ml-2">{category.name}</span>
              </Button>
            ))}
          </div>
        </Card>

        {/* Lab Categories */}
        {Object.entries(labCategories).map(([key, category]) => (
          <Card 
            key={key} 
            id={key}
            className="bg-white rounded-xl border border-stone-100 overflow-hidden"
            data-testid={`category-${key}`}
          >
            {/* Category Header */}
            <button
              onClick={() => toggleCategory(key)}
              className={`w-full p-5 flex items-center justify-between ${category.lightColor} transition-colors hover:opacity-90`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 ${category.color} rounded-lg flex items-center justify-center text-white`}>
                  {category.icon}
                </div>
                <div className="text-left">
                  <h2 className="font-nunito font-bold text-[#2D2D2D]">{category.name}</h2>
                  <p className="text-sm text-[#57534E]">{category.tests.length} tests</p>
                </div>
              </div>
              {expandedCategories[key] ? (
                <ChevronUp className="w-5 h-5 text-[#57534E]" />
              ) : (
                <ChevronDown className="w-5 h-5 text-[#57534E]" />
              )}
            </button>

            {/* Category Content */}
            {expandedCategories[key] && (
              <div className="p-5 border-t border-stone-100">
                <p className="text-[#57534E] mb-4">{category.description}</p>
                <div className="space-y-4">
                  {category.tests.map((test, index) => (
                    <div key={index} className={`${category.lightColor} rounded-lg p-4`}>
                      <h4 className="font-semibold text-[#2D2D2D] mb-1">{test.name}</h4>
                      {test.fullName && test.fullName !== test.name && (
                        <p className="text-xs text-[#57534E] italic mb-2">{test.fullName}</p>
                      )}
                      <div className="space-y-2 text-sm">
                        <div>
                          <span className="font-medium text-[#2D2D2D]">What it measures: </span>
                          <span className="text-[#57534E]">{test.measures}</span>
                        </div>
                        <div>
                          <span className="font-medium text-[#2D2D2D]">PANS Relevance: </span>
                          <span className="text-[#57534E]">{test.relevance}</span>
                        </div>
                        <div>
                          <span className="font-medium text-[#2D2D2D]">Frequency: </span>
                          <span className="text-[#57534E]">{test.frequency}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>
        ))}

        {/* Call to Action */}
        <Card className="bg-accent/10 rounded-xl border-0 p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-accent/20 rounded-xl flex items-center justify-center">
              <TestTube className="w-6 h-6 text-accent" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-[#2D2D2D] mb-1">Track Your Lab Results</h3>
              <p className="text-sm text-[#57534E]">Keep all your child's lab results organized in one place</p>
            </div>
            <Button 
              onClick={() => navigate('/lab-tracker')}
              className="bg-accent hover:bg-accent/90"
              data-testid="go-to-tracker-button"
            >
              Go to Lab Tracker
            </Button>
          </div>
        </Card>

        {/* Bottom Disclaimer */}
        <Card className="bg-stone-100 rounded-xl border-0 p-4">
          <div className="flex items-start gap-2">
            <Info className="w-4 h-4 text-[#57534E] flex-shrink-0 mt-0.5" />
            <p className="text-xs text-[#57534E]">
              Information on this page is compiled from medical literature and expert resources for educational purposes only. 
              It is not a substitute for professional medical advice, diagnosis, or treatment. 
              Reference ranges and test availability may vary by laboratory and geographic location.
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default LabEducation;
